import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Log } from 'ng2-logger';
import { FormBuilder, UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MeasuringReason } from "@app/strain/config/measuring-reasons/models/measuring-reason.model";
import { StrainMeasurementChartModel } from "@app/strain/common/models/view-measurement-models/strain-measurement-chart.model";
import { AppUtils } from "@app/shared/util/app.utils";
import { ExcelPasteTableComponent } from "@app/strain/common/components/excel-paste-table/excel-paste-table.component";
import { StrainMeasurementGageValuesModel } from "@app/strain/common/models/view-measurement-models/strain-measurement-gage-values.model";
import { StrainImageModel } from '@app/strain/common/models/view-measurement-models/strain-image.model';
import { ParsedMeasurementUploadResult } from "@app/strain/planning/service/measurement-excel-import.service";

@Component({
    selector: 'app-strain-measurement-charts-form',
    templateUrl: './strain-measurement-charts-form.component.html',
    styleUrls: ['./strain-measurement-charts-form.component.scss']
})
export class StrainMeasurementChartsFormComponent implements OnInit {
    @ViewChild(ExcelPasteTableComponent, {static: false}) excelTable: ExcelPasteTableComponent;

    _logger = Log.create(StrainMeasurementChartsFormComponent.name);

    @Input() showValidations = false;
    @Input() isLoading = false;
    @Input() reasons: MeasuringReason[];
    @Input() isEditMode: boolean;
    @Input() charts: StrainMeasurementChartModel[];
    @Input() measuringReason: MeasuringReason;
    @Output() excelImportRequested = new EventEmitter<{ index: number; file: File }>();

    public form: UntypedFormGroup;

    isSelected = true;

    files = [];
    isImporting = [];

    constructor(
        private fb: FormBuilder
    ) {
    }

    ngOnInit() {
        this._logger.info("ngOnInit:: Preparing strain configuration form");
        this.initForm(this.isEditMode);
    }

    initForm(isEditMode) {
        this.showValidations = false;

        const hasCharts = isEditMode && this.charts && this.charts.length > 0;

        this.form = this.fb.group({
            id: [null],
            reason: [hasCharts ? this.charts[0].reason : this.measuringReason || null, Validators.required],
            measurements: this.fb.array([], Validators.required)
        });

        if (!this.charts) {
            this.charts = [];
        }

        if (hasCharts) {
            this.charts.forEach(item => {
                this.array('measurements').push(this.buildRow(item))
            });
        } else {
            this.addItem();
        }
    }

    control(name: string): UntypedFormControl {
        return this.form.get(name) as UntypedFormControl;
    }

    array(name: string) {
        return this.form.get(name) as UntypedFormArray;
    }

    arrayControl(index, name) {
        return (this.array('measurements').controls[index] as UntypedFormArray).get(name) as UntypedFormControl;
    }

    addItem() {
        if (this.charts) {
            this.charts.push(new StrainMeasurementChartModel());
        }
        this.array('measurements').push(this.buildRow());
        this.showValidations = false;
    }

    removeItem(index) {
        this.charts = this.charts.filter((item, i) => i !== index);
        this.array('measurements').removeAt(index);
    }

    private buildRow(item = null): UntypedFormGroup {
        if (!item) {
            return this.fb.group({
                title: [null],
                observations: [null],
                measurementChart: [null, Validators.required],
                gageValues: [[], Validators.required],
            });
        } else {
            return this.fb.group({
                title: [item.title],
                observations: [item.observations],
                measurementChart: [item.measurementChart
                && item.measurementChart instanceof StrainImageModel && item.measurementChart.data
                    ? this.getFile(item.measurementChart) : item.measurementChart, Validators.required],
                gageValues: [item.gageValues, Validators.required]
            })
        }
    }

    value() {
        return this.form.value;
    }

    valid() {
        return this.form && this.form.valid && this.reasonsValid();
    }

    reset() {
        this._logger.info("resetForm:: Resetting line details form");
        this.form.reset();
    }

    private reasonsValid() {
        const reason = this.form.value.reason;
        return !(reason.id === 0) || !!reason.other;
    }

    // TODO The copying of the oldRowValue on newRow is performed whilst losing the url. This should be fixed
    onDuplicate(index: any) {
        const oldRowValue = this.array('measurements').controls[index].value;

        if (this.hasMeasurementChart(index)) {
            oldRowValue.measurementChart = this.charts[index].measurementChart;
        }

        this.charts.push(new StrainMeasurementChartModel(oldRowValue));
        const newRow = this.buildRow(oldRowValue);
        this.array('measurements').push(newRow);
        this._logger.info("onDuplicate:: Form state", this.form);
    }

    private hasMeasurementChart(index: any) {
        return this.charts && this.charts[index] && this.charts[index].measurementChart
            && (this.charts[index].measurementChart instanceof StrainImageModel)
            && this.charts[index].measurementChart.url;
    }

    selectMeasurementGraphs(index, file: any) {
        const copy: any = new File([file], file.name, {type: file.type});
        copy.url = file.objectUrl;

        this.array('measurements').controls[index].get('measurementChart').setValue(copy);
        this._logger.info("selectMeasurementGraphs:: Form state", this.form);
    }

    onExcelFileSelected(index: number, event: Event) {
        const input = event.target as HTMLInputElement;
        if (!input.files || input.files.length === 0) {
            return;
        }

        const file = input.files[0];
        input.value = '';

        this.isImporting[index] = true;
        this.excelImportRequested.emit({ index, file });
    }

    selectMeasurementValues(index, values: any) {
        const gageValues = [];
        values.forEach((gage) => {
            const max = gage.max ? gage.max : '';
            const min = gage.min ? gage.min : '';
            if (max !== '' && min !== '') {
                gage.max = max;
                gage.min = min;
                gageValues.push(new StrainMeasurementGageValuesModel(gage));
            }
        })
        this.array('measurements').controls[index].get('gageValues').setValue(gageValues);
        this._logger.info("selectMeasurementValues:: Form state", this.form);
    }

    hasChartImage(index) {
        return !!this.array('measurements').controls[index]
            && !!this.array('measurements').controls[index].get('measurementChart').value;
    }

    hasGageValues(index) {
        return !!this.array('measurements').controls[index]
            && !!this.array('measurements').controls[index].get('gageValues').value;
    }

    changeChartImage(index, imageId: any) {
        const chart = this.charts.find((item, i) => item.measurementChart && item.measurementChart.id === imageId && i === index);

        if (chart) {
            chart.measurementChart = null;
        }

        this.arrayControl(index, 'measurementChart').setValue(null);
    }

    getFile(item) {
        const blob = AppUtils.buildBlobFromDataString(item.data);
        return new File([blob], item.name, {type: item.contentType});
    }

    getGageChart(index) {
        return this.array('measurements').controls[index].get('gageValues').value;
    }

    getChartImage(index) {
        return this.charts && this.charts[index] && this.charts[index].measurementChart && this.charts[index].measurementChart.url
            ? this.charts[index].measurementChart
            : this.array('measurements').controls[index].get('measurementChart').value
    }

    setImporting(index: number, isImporting: boolean) {
        this.isImporting[index] = isImporting;
    }

    applyExcelImport(index: number, result: ParsedMeasurementUploadResult) {
        if (!result) {
            return;
        }
        if (result.gageValues?.length) {
            this.array('measurements').controls[index].get('gageValues').setValue(result.gageValues);
        }
        if (result.chartFile) {
            this.array('measurements').controls[index].get('measurementChart').setValue(result.chartFile);
        }
        this._logger.info('applyExcelImport:: Auto-filled values and chart', this.form);
    }
}
